// @ts-strict-ignore
import React, { useMemo, useRef, useState } from 'react'
import { DialogTitle, DialogContent, DialogActions, Button, IconButton, Grow, DialogContentText, Tooltip, FormControlLabel, Checkbox, Typography, ListSubheader } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import UserSkillsCollection from 'components/UserSkillsCollection'
import ResponsiveDialog from 'components/ResponsiveDialog'
import FocusFadeGroup from 'components/FocusFadeGroup'
import { FormValue } from 'types'
import { getGroupsByIndex } from 'services/job'
import { Create } from '@mui/icons-material'
import { useLazyLoadQuery } from 'react-relay'
import { graphql } from 'relay-runtime'
import { JobSpecializationsFields_Query } from '__generated__/JobSpecializationsFields_Query.graphql'
import { useEdges } from 'hooks/relay'
import FormErrorHint from '../../FormErrorHint'
import useStyles from './styles'

const TransitionComponent: any = React.forwardRef((props: any, ref) => (
  <Grow ref={ref} {...props} />
))

interface IJobSpecializationsFieldsProps {
  editable: boolean
  industryId: FormValue<number>
  specializations: FormValue<{ id: number, name: string, groupIndex?: number }[]>
  label?: string
}

export default function JobSpecializationsFields(props: IJobSpecializationsFieldsProps) {
  const { classes } = useStyles()
  const theme = useTheme()
  const [isEditorOpen, setEditorOpen] = useState(false)

  const { industryId, specializations, editable, label } = props

  // TODO: separate the editable portion of this component to avoid SSR not including the skills in the UI
  const data = useLazyLoadQuery<JobSpecializationsFields_Query>(graphql`
    query JobSpecializationsFields_Query($editable: Boolean!) {
      specializations(first: 100) @include(if: $editable) {
        edges {
          node {
            industry {
              rawId
            }
            rawId
            name
          }
        }
      }
    }
  `, {
    editable: editable || false,
  }, {
    fetchPolicy: 'store-and-network',
  })
  const specializationsData = useEdges(data?.specializations?.edges)

  const selectedSpecializations = specializations.input.value || []
  const specializationsFiltered = specializationsData.filter(specialization => specialization.industry?.rawId === industryId.input.value)
  const container = useRef<HTMLDivElement>(null)
  const containerBounds = container.current?.getBoundingClientRect()
  const selectedIndustry = industryId.input.value
  const groups = useMemo(() => {
    let newGroups = getGroupsByIndex(specializations.input.value)
    if (newGroups.length === 0 || newGroups[newGroups.length - 1].items.length > 0) {
      newGroups.push({ items: [], index: newGroups.length })
    }
    return newGroups
  }, [specializations.input.value])

  const handleSpecializationsCheckToggle = (specialization: typeof specializationsData[number], groupIndex: number) => {
    if (specializations.input.value) {
      if (specializations.input.value.find(s => s?.id === specialization?.rawId && (s?.groupIndex || 0) === (groupIndex || 0))) {
        specializations.input.onChange(specializations.input.value.filter(s => s.id !== specialization?.rawId && (s.groupIndex || 0) !== (groupIndex || 0)))
      } else {
        specializations.input.onChange(specializations.input.value.concat({
          id: specialization.rawId,
          name: specialization.name,
          groupIndex: groupIndex,
        }))
      }
    } else {
      specializations.input.onChange([{ ...specialization, group_index: groupIndex }])
    }
  }

  const handleDialogOpen = () => setEditorOpen(true)
  const handleDialogClose = () => setEditorOpen(false)

  if (editable) {
    return (
      <div ref={container}>
        <FocusFadeGroup focused={false}>
          {specializationsFiltered.length > 0 && (
            <div>
              {selectedSpecializations.length === 0 && (
                <span className={classes.placeholder}>
                  No specializations selected
                </span>
              )}

              <div style={{ display: 'inline-block', maxWidth: 'calc(100% - 72px)' }}>
                <UserSkillsCollection
                  specializations={selectedSpecializations}
                  hideExperience
                />
              </div>

              <Tooltip title="Edit specializations" placement="right">
                <IconButton onClick={handleDialogOpen} className={classes.iconButton} size="large">
                  <Create />
                </IconButton>
              </Tooltip>

              <FormErrorHint message={specializations.meta.touched && specializations.meta.error} />
            </div>
          )}

          {(label && selectedSpecializations.length > 0) && (
            <Typography variant="body1" style={{ marginTop: theme.spacing(-1), marginBottom: theme.spacing(1) }}>
              {label}
            </Typography>
          )}
        </FocusFadeGroup>

        <ResponsiveDialog
          open={isEditorOpen}
          onClose={handleDialogClose}
          TransitionComponent={TransitionComponent}
          TransitionProps={containerBounds ? {
            style: {
              transformOrigin: `${containerBounds.left}px ${containerBounds.top}px`,
            },
          } : undefined}
        >
          <DialogTitle>
            Edit specializations
          </DialogTitle>

          <DialogContent>
            {selectedIndustry && (
            <React.Fragment>
              <DialogContentText className={classes.text}>
                Select the specializations a good candidate for your position should have.
                Note: specializations are dependent on the job department.
                Changing the job department later will clear any set specializations.
              </DialogContentText>

              {groups.map(g => (
                <div key={g.index} className={classes.specializations}>
                  <ListSubheader>Specialization set #{g.index + 1}</ListSubheader>
                  {specializationsFiltered.map(specialization => (
                    <FormControlLabel
                      key={specialization.rawId}
                      label={specialization.name}
                      control={(
                        <Checkbox
                          key={specialization.rawId}
                          color="primary"
                          checked={Boolean(g.items.find(s => s.id === specialization.rawId))}
                          onChange={() => handleSpecializationsCheckToggle(specialization, g.index)}
                          data-cy={`specialization-${specialization.name}`}
                          inputProps={{ 'data-cy': `specialization-${specialization.name}-input` } as any}
                        />
                      )}
                    />
                  ))}
                </div>
              ))}

            </React.Fragment>
            )}

            {!selectedIndustry && (
            <React.Fragment>
              <DialogContentText className={classes.text}>
                Before associating specializations to the job, choose which kind of job/position this is.
              </DialogContentText>
            </React.Fragment>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={handleDialogClose} data-cy="job-job_subtypes-close">
              Ok
            </Button>
          </DialogActions>
        </ResponsiveDialog>
      </div>
    )
  }

  // Non-editable version:

  if (selectedSpecializations.length === 0) {
    return null
  }

  return (
    <div ref={container}>
      <div>
        <UserSkillsCollection
          specializations={selectedSpecializations}
          hideExperience
          style={{ display: 'inline-block' }}
        />

        {(label && selectedSpecializations.length > 0) && (
          <Typography variant="body1" style={{ marginTop: theme.spacing(-1), marginBottom: theme.spacing(1) }}>
            {label}
          </Typography>
        )}
      </div>
    </div>
  )
}
